import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe
} from '@stripe/react-stripe-js';

import { GDButtonV1 } from 'dpl/common/core/GDButton/GDButtonV1';
import { stripePromise } from 'dpl/util/stripePromise';
import { pushToastNotification } from 'dpl/shared/actions/toast_notifications';
import { PAYMENT_CHECKOUT_SESSION_ID } from 'dpl/constants/query_params';
import { sendRudderstackEvent } from 'dpl/util/analytics';
import {
  ANALYTICS_EVENTS,
  ANALYTICS_VIEWS,
  ANALYTICS_VIEW_FEATURES,
  ANALYTICS_VIEW_FEATURE_LOCATIONS
} from 'dpl/constants/analytics';
import LoadingWrapper from 'dpl/components/LoadingWrapper';

export default function PayLaterForm({ clientSecret, ...args }) {
  const options = {
    clientSecret,
    appearance: {
      theme: 'stripe',
      variables: {
        spacingUnit: '6px',
        borderRadius: '8px',
        fontFamily:
          'GT Walsheim Pro, avenir next, avenir, helvetica neue, helvetica, arial, sans-serif'
      }
    }
  };

  return (
    <LoadingWrapper isLoading={!clientSecret}>
      <Elements stripe={stripePromise} options={options}>
        <PayLaterFormElement {...args} />
      </Elements>
    </LoadingWrapper>
  );
}

PayLaterForm.propTypes = {
  clientSecret: PropTypes.string.isRequired
};

/** @param {any} props */
function PayLaterFormElement({
  children = null,
  externalId,
  checkoutSessionIdRef,
  billingDetails,
  paymentRequestId,
  breederProfileId,
  buyerProfileId,
  paymentCategory,
  disabled = false
}) {
  const returnUrl = new URL(
    `/pay/${externalId}/redirect?${PAYMENT_CHECKOUT_SESSION_ID}=${checkoutSessionIdRef.current}`,
    window.location.origin
  ).href;

  const elements = useElements();
  const stripe = useStripe();

  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit(e) {
    try {
      e.preventDefault();
      setSubmitting(true);

      sendRudderstackEvent(
        ANALYTICS_EVENTS.PAYMENT_CHECKOUT_SUBMITTED_BY_BUYER,
        {
          view: ANALYTICS_VIEWS.PAYMENT_DETAILS,
          view_feature: ANALYTICS_VIEW_FEATURES.PAYMENT_CHECKOUT_PAGE,
          view_feature_location:
            ANALYTICS_VIEW_FEATURE_LOCATIONS.PAY_LATER_FORM,
          payment_request_id: paymentRequestId,
          breeder_profile_id: breederProfileId,
          buyer_profile_id: buyerProfileId,
          payment_type: paymentCategory,
          checkout_session_id: checkoutSessionIdRef.current
        }
      );

      const { error } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: returnUrl,
          payment_method_data: {
            billing_details: {
              name: billingDetails.name,
              email: billingDetails.email,
              address: {
                country: 'US'
              }
            }
          }
        }
      });

      if (error && error.type !== 'validation_error') {
        pushToastNotification({
          type: 'error',
          message: error.message
        });
        setSubmitting(false);
      }
    } catch {
      pushToastNotification({
        type: 'error',
        message: 'There was an error submitting your payment'
      });
      setSubmitting(false);
    }
  }

  return (
    <>
      {children}
      <div>
        <h2 className="font-serif font-20 mt6">Pay with Klarna</h2>
        <p className="lh-title font-16 secondary">
          You will be redirected to Klarna to fill out your payment information
          and choose your payment plan.
        </p>
      </div>
      <div className="mt3">
        <PaymentElement
          options={{
            fields: {
              billingDetails: {
                name: 'never',
                email: 'never',
                address: { country: 'never' }
              }
            },
            terms: {
              applePay: 'never',
              auBecsDebit: 'never',
              bancontact: 'never',
              card: 'never',
              cashapp: 'never',
              googlePay: 'never',
              ideal: 'never',
              paypal: 'never',
              sepaDebit: 'never',
              sofort: 'never',
              usBankAccount: 'never'
            }
          }}
        />
      </div>
      <GDButtonV1
        disabled={submitting || disabled}
        type="submit"
        onClick={handleSubmit}
        className="PayNowButton w-100"
      >
        Complete payment with Klarna
      </GDButtonV1>
    </>
  );
}

PayLaterFormElement.propTypes = {
  children: PropTypes.node,
  externalId: PropTypes.string.isRequired,
  checkoutSessionIdRef: PropTypes.shape({
    current: PropTypes.string
  }).isRequired,
  billingDetails: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string
  }).isRequired,
  paymentRequestId: PropTypes.number.isRequired,
  breederProfileId: PropTypes.number.isRequired,
  buyerProfileId: PropTypes.number.isRequired,
  paymentCategory: PropTypes.string.isRequired,
  disabled: PropTypes.bool
};
